/**
 * Consumer Assistance Wizard — 4 steps.
 *   1. What happened + when + channel
 *   2. Which institution + advisor
 *   3. Desired outcome
 *   4. Evidence
 * Final: submit → get reference number → confetti-lite state
 *
 * Altara does not investigate. This wizard packages the consumer's story
 * into a Consumer Assistance Package and transfers it to the institution.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { loadWallet } from "@/lib/wallet-storage";
import {
  AlertTriangle, Building2, Target, FileText, ShieldCheck, ArrowRight,
  ArrowLeft, Loader2, X, Upload, CheckCircle2, Link as LinkIcon, StickyNote,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/assistance`;

const KIND_META = {
  complaint: { icon: FileText, title: "Complaint assistance", intro: "We help you explain what happened and package it into a clear, complete complaint for the institution to action. Altara doesn't investigate — the institution owns the outcome." },
  scam: { icon: AlertTriangle, title: "Scam assistance", intro: "We help you collect evidence, verify institution details, and generate a package the institution's fraud team can act on. Altara doesn't investigate — the institution decides on any investigation." },
  unfair_treatment: { icon: Target, title: "Unfair treatment assistance", intro: "We help you articulate what you believe was unfair and match it to your rights under TCF. The institution's complaints process picks it up from there." },
  payment_concern: { icon: FileText, title: "Payment concern assistance", intro: "We help you organise the payment details and gather what the institution will need to trace or dispute it." },
  other: { icon: FileText, title: "Consumer assistance", intro: "We help you articulate what happened and package it for the institution." },
};


function getDeviceId() {
  let id = localStorage.getItem("altara.device_id");
  if (!id) { id = "dev_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36).slice(-6); localStorage.setItem("altara.device_id", id); }
  return id;
}


export default function WalletAssistanceWizard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const kindParam = params.get("kind") || "complaint";
  const kind = KIND_META[kindParam] ? kindParam : "other";
  const meta = KIND_META[kind];
  const Icon = meta.icon;

  const [wallet, setWallet] = useState(null);
  const [step, setStep] = useState(1);
  const [req, setReq] = useState(null);            // AssistanceRequest from server
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { setWallet(loadWallet()); }, []);
  const consumerName = wallet?.name || "";

  // Local draft form fields
  const [form, setForm] = useState({
    what_happened: "", when_happened: "", channel: "email",
    institution_name: "", advisor_name: "", account_or_policy_ref: "",
    desired_outcome: "",
  });

  const ensureCreated = async () => {
    if (req) return req;
    if (!consumerName) { setError("Please set your name in Profile & settings first."); return null; }
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/requests`, {
        device_id: getDeviceId(),
        consumer_display_name: consumerName,
        kind,
        ...form,
      });
      setReq(data);
      return data;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create draft");
      return null;
    } finally { setBusy(false); }
  };

  const patch = async (fields) => {
    if (!req) return;
    setBusy(true); setError("");
    try {
      const { data } = await axios.patch(`${API}/requests/${req.id}?device_id=${getDeviceId()}`, fields);
      setReq(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const next = async () => {
    if (step === 1) {
      const created = await ensureCreated();
      if (!created) return;
      setStep(2); return;
    }
    if (step === 2) {
      await patch({ institution_name: form.institution_name, advisor_name: form.advisor_name, account_or_policy_ref: form.account_or_policy_ref });
      setStep(3); return;
    }
    if (step === 3) {
      await patch({ desired_outcome: form.desired_outcome });
      setStep(4); return;
    }
  };
  const back = () => setStep(s => Math.max(1, s - 1));

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/requests/${req.id}/submit?device_id=${getDeviceId()}`);
      setSubmitted(data);
    } catch (e) { setError(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const canProceed = useMemo(() => {
    if (step === 1) return form.what_happened.trim().length >= 10;
    if (step === 2) return form.institution_name.trim().length >= 2;
    if (step === 3) return form.desired_outcome.trim().length >= 5;
    return true;
  }, [step, form]);

  if (submitted) return <SubmittedState submitted={submitted} onDone={() => navigate("/wallet/assistance")} />;

  return (
    <WalletShell activeTab="safety">
      <div className="max-w-3xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-6" data-testid="assistance-wizard">
        <header>
          <button onClick={() => navigate("/wallet")} className="text-[11px] text-slate-500 hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Home</button>
          <div className="mt-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: GOLD, color: NAVY }}><Icon className="w-5 h-5" /></div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Consumer Assistance</div>
              <h1 className="font-display text-2xl lg:text-3xl font-semibold" style={{ color: NAVY }}>{meta.title}</h1>
            </div>
          </div>
          <p className="mt-3 text-[13px] text-slate-600 leading-relaxed">{meta.intro}</p>
        </header>

        <StepIndicator step={step} />

        {step === 1 && (
          <StepCard title="Step 1 · What happened?">
            <Field label="Describe what happened — in your own words">
              <textarea rows={5} value={form.what_happened} onChange={e => setForm({ ...form, what_happened: e.target.value })} data-testid="w-what-happened" placeholder="Give us the story — dates, amounts, what was said, what you expected vs what happened…" className="w-full rounded-lg border border-slate-200 p-3 text-sm" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="When did this happen?">
                <input value={form.when_happened} onChange={e => setForm({ ...form, when_happened: e.target.value })} data-testid="w-when" placeholder="e.g. 2 May 2026, last Tuesday" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </Field>
              <Field label="Where did it happen?">
                <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })} data-testid="w-channel" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="phone_call">Phone call</option>
                  <option value="branch">Branch</option>
                  <option value="app">Bank app</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>
          </StepCard>
        )}

        {step === 2 && (
          <StepCard title="Step 2 · Who is involved?">
            <Field label="Institution">
              <input value={form.institution_name} onChange={e => setForm({ ...form, institution_name: e.target.value })} data-testid="w-institution" placeholder="e.g. ABC Bank" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </Field>
            <Field label="Advisor / staff member (optional)">
              <input value={form.advisor_name} onChange={e => setForm({ ...form, advisor_name: e.target.value })} data-testid="w-advisor" placeholder="e.g. Thabo Mokoena" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </Field>
            <Field label="Account or policy reference (optional)">
              <input value={form.account_or_policy_ref} onChange={e => setForm({ ...form, account_or_policy_ref: e.target.value })} data-testid="w-account-ref" placeholder="e.g. Policy AL-2026-…" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </Field>
          </StepCard>
        )}

        {step === 3 && (
          <StepCard title="Step 3 · What outcome do you want?">
            <Field label="Describe the outcome you're looking for">
              <textarea rows={5} value={form.desired_outcome} onChange={e => setForm({ ...form, desired_outcome: e.target.value })} data-testid="w-outcome" placeholder="e.g. Refund of R…, apology, correction on my credit record, escalation to the Ombud…" className="w-full rounded-lg border border-slate-200 p-3 text-sm" />
            </Field>
            <div className="text-[11px] text-slate-500">Being specific helps the institution act faster.</div>
          </StepCard>
        )}

        {step === 4 && req && (
          <EvidenceStep req={req} onChange={setReq} />
        )}

        {error && <div className="rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800 p-3" data-testid="wizard-error">{error}</div>}

        <div className="flex items-center justify-between">
          <button onClick={back} disabled={step === 1} className="text-[13px] text-slate-500 disabled:opacity-30 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          {step < 4 ? (
            <button onClick={next} disabled={busy || !canProceed} data-testid="wizard-next" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-40" style={{ background: NAVY, color: "white" }}>
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </button>
          ) : (
            <button onClick={submit} disabled={busy} data-testid="wizard-submit" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-40" style={{ background: GOLD, color: NAVY }}>
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><ShieldCheck className="w-4 h-4" /> Submit to institution</>}
            </button>
          )}
        </div>

        <FooterDisclaimer />
      </div>
    </WalletShell>
  );
}


function StepIndicator({ step }) {
  const items = ["What happened", "Institution", "Outcome", "Evidence"];
  return (
    <div className="flex items-center gap-2" data-testid="wizard-steps">
      {items.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <div key={label} className="flex-1 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0" style={{
              background: done ? GOLD : active ? NAVY : "#E2E8F0",
              color: done ? NAVY : active ? "white" : "#94A3B8",
            }}>
              {done ? "✓" : n}
            </div>
            <span className={`text-[11px] font-medium ${active ? "" : "text-slate-500"}`} style={active ? { color: NAVY } : undefined}>{label}</span>
            {i < items.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
          </div>
        );
      })}
    </div>
  );
}


function StepCard({ title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 space-y-4">
      <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>{title}</div>
      {children}
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}


function EvidenceStep({ req, onChange }) {
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [urlText, setUrlText] = useState("");

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/requests/${req.id}/evidence-note?device_id=${getDeviceId()}`, { kind: "note", label: "Note", note: noteText });
      onChange(data); setNoteText("");
    } finally { setBusy(false); }
  };
  const addUrl = async () => {
    if (!urlText.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/requests/${req.id}/evidence-note?device_id=${getDeviceId()}`, { kind: "url", label: "Link", url: urlText });
      onChange(data); setUrlText("");
    } finally { setBusy(false); }
  };
  const addFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("device_id", getDeviceId());
      fd.append("label", file.name);
      const { data } = await axios.post(`${API}/requests/${req.id}/evidence-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data);
    } finally { setBusy(false); }
  };
  const remove = async (evidence_id) => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/requests/${req.id}/evidence/${evidence_id}?device_id=${getDeviceId()}`);
      onChange(data);
    } finally { setBusy(false); }
  };

  return (
    <StepCard title="Step 4 · Evidence">
      <p className="text-[12px] text-slate-600">Attach anything that supports your story — SMS screenshots, statement pages, emails, or a link. Files are hashed for integrity and are shared with the institution together with your reference number.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="rounded-xl border border-slate-200 p-3 cursor-pointer flex flex-col gap-1 hover:bg-slate-50" data-testid="w-add-file">
          <Upload className="w-4 h-4" style={{ color: NAVY }} />
          <span className="text-[12px] font-medium" style={{ color: NAVY }}>Attach a file</span>
          <span className="text-[10px] text-slate-500">Max 10 MB · hashed for integrity</span>
          <input type="file" className="hidden" onChange={e => addFile(e.target.files?.[0])} />
        </label>
        <div className="rounded-xl border border-slate-200 p-3 space-y-1.5">
          <LinkIcon className="w-4 h-4" style={{ color: NAVY }} />
          <input value={urlText} onChange={e => setUrlText(e.target.value)} data-testid="w-url-input" placeholder="Paste a link (e.g. suspicious URL)" className="w-full rounded-md border border-slate-200 px-2 py-1 text-[12px]" />
          <button onClick={addUrl} disabled={busy || !urlText.trim()} data-testid="w-add-url" className="text-[11px] font-semibold hover:underline disabled:opacity-40" style={{ color: NAVY }}>Add link →</button>
        </div>
        <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3 space-y-1.5">
          <StickyNote className="w-4 h-4" style={{ color: NAVY }} />
          <textarea rows={2} value={noteText} onChange={e => setNoteText(e.target.value)} data-testid="w-note-input" placeholder="Add a note or observation…" className="w-full rounded-md border border-slate-200 px-2 py-1 text-[12px]" />
          <button onClick={addNote} disabled={busy || !noteText.trim()} data-testid="w-add-note" className="text-[11px] font-semibold hover:underline disabled:opacity-40" style={{ color: NAVY }}>Add note →</button>
        </div>
      </div>
      {(req.evidence || []).length > 0 && (
        <div className="space-y-2 pt-2" data-testid="w-evidence-list">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Attached · {req.evidence.length}</div>
          {req.evidence.map(e => (
            <div key={e.evidence_id} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-[12px]">
              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{e.kind}</span>
              <span className="flex-1 min-w-0 truncate" style={{ color: NAVY }}>{e.filename || e.url || e.note || e.label}</span>
              {e.sha256 && <span className="font-mono text-[9px] text-slate-400 truncate max-w-[120px]">{e.sha256.slice(0, 10)}…</span>}
              <button onClick={() => remove(e.evidence_id)} className="text-slate-400 hover:text-rose-600"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
    </StepCard>
  );
}


function SubmittedState({ submitted, onDone }) {
  const req = submitted.request;
  return (
    <WalletShell activeTab="safety">
      <div className="max-w-3xl mx-auto px-4 lg:px-10 py-8 lg:py-10" data-testid="wizard-submitted">
        <div className="rounded-3xl bg-white shadow-xl border overflow-hidden" style={{ borderColor: `${GOLD}55` }}>
          <div className="px-6 lg:px-10 py-8 text-white flex items-center gap-4" style={{ background: NAVY }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: GOLD }}>
              <CheckCircle2 className="w-6 h-6" style={{ color: NAVY }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Successfully submitted</div>
              <h1 className="font-display text-2xl font-semibold">Your Consumer Assistance Package is with {req.institution_name}</h1>
            </div>
          </div>
          <div className="p-6 lg:p-10 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Stat label="Reference number" value={submitted.reference_number} mono />
              <Stat label="Institution" value={req.institution_name} />
              <Stat label="Status" value="Submitted" />
            </div>
            <div className="rounded-2xl p-4" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}55` }}>
              <div className="text-[12px] leading-relaxed text-slate-800">
                <strong style={{ color: NAVY }}>The institution will contact you directly.</strong>{" "}
                Altara does not investigate — the institution remains responsible for ongoing communication.
                Keep your reference number handy for follow-ups.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={onDone} data-testid="submitted-done" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold" style={{ background: NAVY, color: "white" }}>
                See all my assistance requests <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </WalletShell>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className={`text-[15px] font-semibold ${mono ? "font-mono" : ""}`} style={{ color: NAVY }}>{value || "—"}</div>
    </div>
  );
}

function FooterDisclaimer() {
  return (
    <div className="rounded-xl p-3 border" style={{ background: "#F1F5F9", borderColor: "#E2E8F0" }}>
      <div className="text-[11px] text-slate-600 leading-relaxed">
        <strong>What Altara does:</strong> we help you articulate, organise and package what happened.{" "}
        <strong>What Altara does not:</strong> investigate, adjudicate, or manage the case.
        The institution remains responsible for the outcome.
      </div>
    </div>
  );
}
